import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiRepository } from '../../../../shared/infrastructure/api.repository';
import {
  QualityControlRepositoryPort,
  QualityControlListPagedRequest,
  QualityControlListPagedResponse,
  QualityControlGetListPagedRequest,
  QcDocumentListPagedRequest,
  QcDocumentListPagedResponse,
  OrderQcDocumentListPagedRequest,
  QcDocumentUpload,
} from '../../domain/repositories/quality-control.repository.port';
import { QualityControl } from '../../domain/entities/quality-control.entity';
import { QcVerifications } from '../../domain/entities/quality-control-verification.entity';
import { QcAsset } from '../../domain/entities/quality-control-asset.entity';
import { AssetUseType } from '../../domain/entities/asset-use-type.entity';
import { QualityControlRequiredFields } from '../../domain/entities/quality-control-required-field.entity';
import { KoOption } from '../../domain/entities/ko-option.entity';
import { QualityControlOriginOption } from '../../domain/entities/quality-control-origin.entity';
import { QualityControlInterfaceData } from '../../domain/entities/quality-control-interface-data.entity';
import { EditAssetItemDto } from '../../application/dtos/edit-assets.dto';
import { EditAppraisalValidationDto, EditAssetValidationDto } from '../../application/dtos/edit-validations.dto';
import {
  QualityControlMapper,
  QualityControlListPagedApiResponse,
  QualityControlDetailApiResponse,
  QcValidationsApiResponse,
  QcAssetsApiResponse,
  AssetUseTypeApiResponse,
  QualityControlRequiredFieldsApiResponse,
  KoOptionApiResponse,
  QcFinishResultApiResponse,
  QualityControlOriginApiResponse,
  QualityControlInterfaceDataApiResponse,
} from '../mappers/quality-control.mapper';
import { QualityControlFinishResult } from '../../domain/entities/quality-control-finish-result.entity';
import { QcDocumentMapper, QcDocumentListPagedApiResponse } from '../mappers/quality-control-document.mapper';
import { CreateOrderWithQualityControlDocumentDto } from 'app/modules/orders/application/dtos/create-order-with-quality-control.dto';
import { QcCommunicationHistoryMapper, QcCommunicationHistoryApiResponse } from '../mappers/qc-communication-history.mapper';
import { QcCommunicationHistoryItem } from '../../domain/entities/qc-communication-history.entity';

@Injectable({ providedIn: 'root' })
export class QualityControlRepository extends ApiRepository implements QualityControlRepositoryPort {
  private readonly baseUrl = '/qualitycontrols';

  getListPaged(request: QualityControlGetListPagedRequest): Observable<QualityControlListPagedResponse> {
    return this.post<QualityControlListPagedApiResponse, QualityControlGetListPagedRequest>(
      `${this.baseUrl}/GetListPaged`,
      request,
    ).pipe(
      map((response) => ({
        items: QualityControlMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
      })),
    );
  }

  getListPagedByOrder(request: QualityControlListPagedRequest): Observable<QualityControlListPagedResponse> {
    return this.post<QualityControlListPagedApiResponse, object>(
      `${this.baseUrl}/GetListPagedByOrder?orderId=${request.orderId}`,
      {
        orderField: request.orderField,
        ascendingOrder: request.ascendingOrder,
        page: request.page,
        pageSize: request.pageSize,
        filter: request.filter,
      },
    ).pipe(
      map((response) => ({
        items: QualityControlMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
      })),
    );
  }

  getDetails(id: string): Observable<QualityControl> {
    return this.get<QualityControlDetailApiResponse>(`${this.baseUrl}/${id}`).pipe(
      map((response) => QualityControlMapper.toDomain({ ...response })),
    );
  }

  create(orderId: string, appraisalCompany?: number, appraisalPurpose?: number, appraisalReportNumber?: string | null, appraisalIssueDate?: Date | null, appraiserName?: string | null, documents?: CreateOrderWithQualityControlDocumentDto[], origin?: number | null): Observable<string> {
    const formData = new FormData();
    formData.append('orderId', orderId.toString());
    if (appraisalCompany != null) formData.append('appraisalCompany', appraisalCompany.toString());
    if (appraisalPurpose != null) formData.append('appraisalPurpose', appraisalPurpose.toString());
    if (appraisalReportNumber) formData.append('appraisalReportNumber', appraisalReportNumber);
    if (appraisalIssueDate) formData.append('appraisalIssueDate', appraisalIssueDate.toISOString());
    if (appraiserName) formData.append('appraiserName', appraiserName);
    if (origin != null) formData.append('origin', origin.toString());

    if (documents?.length) {
      documents.forEach((doc, i) => {
        formData.append(`documents[${i}].formFile`, doc.file);
        formData.append(`documents[${i}].documentType`, doc.documentType.toString());
      });
    }

    return this.http.post<string>(`${this.baseApiUrl}${this.baseUrl}`, formData);
  }

  update(
    qualityControlId: string,
    appraisalCompany?: number,
    appraisalPurpose?: number,
    appraisalReportNumber?: string | null,
    appraisalIssueDate?: Date | null,
    appraiserName?: string | null,
    origin?: number | null,
  ): Observable<void> {
    return this.put<void, object>(`${this.baseUrl}/${qualityControlId}`, {
      appraisalCompany: appraisalCompany ?? null,
      appraisalPurpose: appraisalPurpose ?? null,
      appraisalReportNumber: appraisalReportNumber ?? null,
      appraisalIssueDate: appraisalIssueDate ? appraisalIssueDate.toISOString() : null,
      appraiserName: appraiserName ?? null,
      origin: origin ?? null,
    });
  }
// lo mismo para orders
  getAssets(qualityControlId: string): Observable<QcAsset[]> {
    return this.get<QcAssetsApiResponse>(
      `${this.baseUrl}/${qualityControlId}/assets/GetAll`
    ).pipe(
      map((response) => QualityControlMapper.toAssetsDomain(response)),
    );
  }

  editAssets(qualityControlId: string, assets: EditAssetItemDto[]): Observable<void> {
    return this.put<void, object>(`${this.baseUrl}/${qualityControlId}/Assets`, {
      assets,
    });
  }

  getValidations(qualityControlId: string): Observable<QcVerifications> {
    return this.get<QcValidationsApiResponse>(`${this.baseUrl}/${qualityControlId}/Verifications`).pipe(
      map((response) => QualityControlMapper.toValidationsDomain(response)),
    );
  }

  editValidations(
    qualityControlId: string,
    appraisalValidation: EditAppraisalValidationDto,
    assetValidations: EditAssetValidationDto[],
  ): Observable<void> {
    return this.put<void, object>(`${this.baseUrl}/${qualityControlId}/Verifications`, {
      qualityControlVerifications: appraisalValidation,
      assetsVerifications: assetValidations,
    });
  }

  startValidation(qualityControlId: string): Observable<void> {
    return this.put<void, object>(`${this.baseUrl}/${qualityControlId}/StartValidation`, {});
  }

  finishValidation(qualityControlId: string): Observable<QualityControlFinishResult> {
    return this.put<QcFinishResultApiResponse, null>(`${this.baseUrl}/${qualityControlId}/FinishValidation`, null).pipe(
      map((response) => QualityControlMapper.toFinishResultDomain(response)),
    );
  }

  assignTechnical(qualityControlId: string, userId: string): Observable<void> {
    return this.put<void, object>(`${this.baseUrl}/${qualityControlId}/AssignTechnical`, { userId });
  }

  getQcDocuments(request: QcDocumentListPagedRequest): Observable<QcDocumentListPagedResponse> {
    return this.post<QcDocumentListPagedApiResponse, object>(
      `${this.baseUrl}/${request.qualityControlId}/Documents/GetListPaged`,
      {
        page: request.page,
        pageSize: request.pageSize,
        orderField: request.orderField,
        ascendingOrder: request.ascendingOrder,
        filter: request.filter,
      },
    ).pipe(
      map((response) => ({
        items: QcDocumentMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
      })),
    );
  }

  getQcDocumentsByOrder(request: OrderQcDocumentListPagedRequest): Observable<QcDocumentListPagedResponse> {
    return this.post<QcDocumentListPagedApiResponse, object>(
      `/qualitycontrols/Documents/GetListPagedByOrder?orderId=${request.orderId}`,
      {
        page: request.page,
        pageSize: request.pageSize,
        orderField: request.orderField,
        ascendingOrder: request.ascendingOrder,
        filter: request.filter,
      },
    ).pipe(
      map((response) => ({
        items: QcDocumentMapper.toDomainList(response.items ?? []),
        pageNumber: response.pageNumber,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalItemsCount: response.totalItemsCount,
      })),
    );
  }

  uploadQcDocuments(qualityControlId: string, documents: QcDocumentUpload[]): Observable<void> {
    const formData = new FormData();
    documents.forEach((doc, i) => {
      formData.append(`files[${i}].formFile`, doc.file);
      formData.append(`files[${i}].documentType`, doc.documentType.toString());
      formData.append(`files[${i}].documentOrigin`, doc.documentOrigin.toString());
      if (doc.qualityControlAssetId) {
        formData.append(`files[${i}].qualityControlAssetId`, doc.qualityControlAssetId);
      }
    });
    return this.http.post<void>(`${this.baseApiUrl}/qualityControls/${qualityControlId}/documents`, formData);
  }

  deleteQcDocument(documentId: string): Observable<void> {
    return this.delete<void>(`/qualityControls/document/${documentId}`);
  }

  getAssetUseTypes(contractId?: number | null): Observable<AssetUseType[]> {
    let params = new HttpParams();
    if (contractId != null) {
      params = params.set('contractId', String(contractId));
    }
    return this.get<AssetUseTypeApiResponse[]>('/enums/AssetUseTypes', params).pipe(
      map(items => QualityControlMapper.toAssetUseTypesDomain(items)),
    );
  }

  getQualityControlRequiredFields(contractId?: number | null): Observable<QualityControlRequiredFields> {
    let params = new HttpParams();
    if (contractId != null) {
      params = params.set('contractId', String(contractId));
    }
    return this.get<QualityControlRequiredFieldsApiResponse>(`${this.baseUrl}/RequiredFields`, params).pipe(
      map(raw => QualityControlMapper.toQualityControlRequiredFieldsDomain(raw)),
    );
  }

  deliverQualityControl(qualityControlId: string): Observable<void> {
    return this.put<void, null>(`${this.baseUrl}/${qualityControlId}/Deliver`, null);
  }

  // TODO: reemplazar la URL cuando back confirme el endpoint real
  getKoOptions(contractId?: number | null): Observable<KoOption[]> {
    let params = new HttpParams();
    if (contractId != null) {
      params = params.set('contractId', String(contractId));
    }
    return this.get<KoOptionApiResponse[]>('/enums/koResults', params).pipe(
      map(items => QualityControlMapper.toKoOptionsDomain(items)),
    );
  }

  getQualityControlOrigins(): Observable<QualityControlOriginOption[]> {
    return this.get<QualityControlOriginApiResponse[]>('/enums/QualityControlOrigin').pipe(
      map(items => QualityControlMapper.toQualityControlOriginsDomain(items)),
    );
  }

  getInterfaceDatas(qualityControlId: string, contractId?: number | null): Observable<QualityControlInterfaceData> {
    let params = new HttpParams();
    if (contractId != null) {
      params = params.set('contractId', String(contractId));
    }
    return this.get<QualityControlInterfaceDataApiResponse>(`${this.baseUrl}/${qualityControlId}/InterfaceDatas`, params).pipe(
      map(raw => QualityControlMapper.toInterfaceDataDomain(raw)),
    );
  }

  getCommunicationHistory(qualityControlId: string): Observable<QcCommunicationHistoryItem[]> {
    return this.get<QcCommunicationHistoryApiResponse>(`${this.baseUrl}/${qualityControlId}/Communications`).pipe(
      map(response => (response.items ?? []).map(QcCommunicationHistoryMapper.toDomain)),
    );
  }
}
